import React, { useMemo } from "react";
import PropTypes from "prop-types";

import InternalUserDashboard from "../../../globals/components/InternalUserDashboard";
import { useIuHomeData } from "../hooks/useIuHomeData";
import { buildDashboardLinkGroups, IU_HOME_VARIANT } from "../pgHomeConfig";
import { mapInternalUserDashboardLinkIcons } from "./dashboardLinkIcons";
import { isDuplicateAccountsMergeUiEnabled } from "../../../globals/utils/duplicateAccountsMergeFeature";
import PgHomeActivitySection from "./PgHomeActivitySection";
import PgHomeIndemnityDialogs from "./PgHomeIndemnityDialogs";
import HomeShellBase, { buildHomeIndemnityDialogProps } from "./HomeShellBase";
import HomeRightColumnSections from "./HomeRightColumnSections";

/** Internal-user home orchestration shell — data in {@link useIuHomeData}. */
export default function IuHomeBase({ variant = IU_HOME_VARIANT.internalUser }) {
  const home = useIuHomeData(variant);
  const links = useMemo(() => {
    const groups = buildDashboardLinkGroups({
      variant,
      userFeatures: home.userFeatures,
      duplicateAccountsMergeEnabled: isDuplicateAccountsMergeUiEnabled(),
    });
    return mapInternalUserDashboardLinkIcons(groups);
  }, [variant, home.userFeatures]);

  const { cfg } = home;
  const sections = cfg.sections || {};

  return (
    <HomeShellBase
      dashboard={<InternalUserDashboard title={cfg.dashboardTitle} links={links} />}
      contentSx={{ px: 3 }}
      leftColumn={
        sections.activity ? (
          <PgHomeActivitySection
            activityCopy={cfg.activity}
            activities={home.activity.activities}
            loadingActivities={home.activity.loadingActivities}
            hasMoreActivities={home.activity.hasMoreActivities}
            selectedActivity={home.activity.selectedActivity}
            onSelectActivity={home.activity.setSelectedActivity}
            onLoadMore={home.activity.loadMoreActivities}
            recentActiveCount={home.activity.recentActiveCount}
          />
        ) : null
      }
      rightColumn={
        <HomeRightColumnSections
          sections={sections}
          cfg={cfg}
          indemnity={home.indemnity}
          drafts={home.drafts}
          assignment={home.assignment}
          showUnpaidBonds
        />
      }
      footer={<PgHomeIndemnityDialogs {...buildHomeIndemnityDialogProps(home)} />}
    />
  );
}

IuHomeBase.propTypes = {
  variant: PropTypes.oneOf([IU_HOME_VARIANT.internalUser, IU_HOME_VARIANT.admin]),
};
